import {
    Coordinate2d,
    SimpleReelModelDelegate,
    SimpleReelModelEventData,
    SimpleReelModelState,
    SimpleReelModelSymbolData,
    SimpleSymbolState
} from "./SimpleReelModel";

import {gsap} from "gsap";
import {GSAPUtils} from "../../../game/utils/GSAPUtils";
import {TheCageStatics} from "../../../game/config/TheCageStatics";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {MatrixUtils} from "../../../game/utils/MatrixUtils";
import {TheCage} from "../../../game/TheCage";
import {StateMatrix} from "../../../game/utils/StateMatrix";
import {ReelUtils} from "../../../game/utils/ReelUtils";
import {TheCageSound} from "../../../game/sound/TheCageSound";


export interface SymbolStopCallBackData {
    isNearWin:boolean;
    isSingleSymbolDrop:boolean;
}

/**
 * Created by jonas on 2022-06-10.
 */

export class SimpleMatrixAvalancheModel {

    public readonly events:APIEventSystem | undefined;
    protected _symbols:SimpleReelModelSymbolData[][];
    protected _symbolTargetPositions:Coordinate2d[][];
    protected _delegate:SimpleReelModelDelegate;
    protected dropOutTl?:gsap.core.Timeline;


    constructor(columns:number, delegate:SimpleReelModelDelegate, triggerEvents:boolean = false) {
        this._symbols = [];
        this._delegate = delegate;
        if (triggerEvents) {
            this.events = SlotGame.apiPlugin.eventSystemFactory.create();
            //To prevent event system from saving once callbacks if no on callbacks is set.
            this.events.on("ReelStateChange", (data:SimpleReelModelEventData) => this.onReelStateChange(data));
        }
    }

    protected createNewSymbol(name:string, column:number, row:number, state:SimpleReelModelState):SimpleReelModelSymbolData {
        return {
            name:name,
            positionable:this._delegate.createSymbol(name, state, {reelID:column, position:row}),
            reelPosition:row,
            removed:false,
            reelId:column
        }
    }

    protected clearSymbols(matrix:SimpleReelModelSymbolData[][]) {
        let reel;
        let symbol;
        while (reel = matrix.pop()) {
            while (symbol = reel.pop()) {
                this._delegate.removeSymbol(symbol);
            }
        }

    }

    private onReelStateChange(data:SimpleReelModelEventData):void {
        this.events?.trigger(data.state, data);
    }

    public init(symbolNames:string[][], symbolPositions:Coordinate2d[][]) {
        this.clearSymbols(this._symbols);

        this._symbolTargetPositions = symbolPositions;

        for (let i:number = 0; i < symbolNames.length; i++) {
            this._symbols[i] = [];
            for (let j:number = 0; j < symbolNames[i].length; j++) {
                const symbol = this.createNewSymbol(symbolNames[i][j], i, j, SimpleReelModelState.IDLE);
                if (symbol.name == "BLANK"){
                    symbol.removed = true;
                }
                symbol.positionable.setAvalancheState(SimpleSymbolState.NORMAL)
                symbol.positionable.position.x = this._symbolTargetPositions[i][j].x;
                symbol.positionable.position.y = this._symbolTargetPositions[i][j].y;
                this._symbols[i].push(symbol);
            }
        }
    }

    private animate(state:SimpleSymbolState, reverse:boolean):gsap.core.Timeline {
        let drops:gsap.core.Timeline[] = [];
        for (let i:number = 0; i < this._symbols.length; i++) {
            for (let j:number = 0; j < this._symbols[i].length; j++) {
                const symbol = this._symbols[i][j];
                if (symbol == undefined) {
                    continue;
                }
                if (symbol.reelId == i && symbol.reelPosition == j) {
                    continue;
                }
                symbol.reelId = i;
                symbol.reelPosition = j;

                if (symbol.removed){
                    continue;
                }

                const dropTl = gsap.timeline();
                dropTl.add(() => {
                    symbol.positionable.setAvalancheState(state);
                })
                dropTl.add(gsap.to(symbol.positionable.position, {
                    duration:TheCageStatics.COLLAPSE_DURATION,
                    x:this._symbolTargetPositions[i][j].x,
                    y:this._symbolTargetPositions[i][j].y,
                    ease:"power1.in"
                }));
                dropTl.add(() => {
                    symbol.positionable.setAvalancheState(SimpleSymbolState.NORMAL);
                })
                dropTl.add(() => {
                    TheCage.soundPlayer.playSound(TheCageSound.SYM_COLLAPSE.next())
                });

                drops.push(dropTl);
            }
        }

        if (reverse) {
            drops = drops.reverse();
        }

        return GSAPUtils.createTimelineFromArray(drops, `<${TheCageStatics.COLLAPSE_DELAY}`);
    }

    protected flipLeft(symbols:any[][]):any[][] {
        return [
            [symbols[0][2], symbols[1][3]],
            [symbols[0][1], symbols[1][2], symbols[2][2], symbols[3][3]],
            [symbols[0][0], symbols[1][1], symbols[2][1], symbols[3][2], symbols[4][2], symbols[5][3]],
            [symbols[1][0], symbols[2][0], symbols[3][1], symbols[4][1], symbols[5][2], symbols[6][2]],
            [symbols[3][0], symbols[4][0], symbols[5][1], symbols[6][1]],
            [symbols[5][0], symbols[6][0]],
        ]
    }

    protected flipBackFromLeft(symbols:any[][]):any[][] {
        return [
            [symbols[2][0], symbols[1][0], symbols[0][0]],
            [symbols[3][0], symbols[2][1], symbols[1][1], symbols[0][1]],
            [symbols[3][1], symbols[2][2], symbols[1][2]],
            [symbols[4][0], symbols[3][2], symbols[2][3], symbols[1][3]],
            [symbols[4][1], symbols[3][3], symbols[2][4],],
            [symbols[5][0], symbols[4][2], symbols[3][4], symbols[2][5]],
            [symbols[5][1], symbols[4][3], symbols[3][5],],
        ]
    }

    protected flipRight(symbols:any[][]):any[][] {
        return [
            [symbols[1][0], symbols[0][0]],
            [symbols[3][0], symbols[2][0], symbols[1][1], symbols[0][1]],
            [symbols[5][0], symbols[4][0], symbols[3][1], symbols[2][1], symbols[1][2], symbols[0][2]],
            [symbols[6][0], symbols[5][1], symbols[4][1], symbols[3][2], symbols[2][2], symbols[1][3]],
            [symbols[6][1], symbols[5][2], symbols[4][2], symbols[3][3]],
            [symbols[6][2], symbols[5][3],],
        ]
    }

    protected flipBackFromRight(symbols:any[][]):any[][] {
        return [
            [symbols[0][1], symbols[1][3], symbols[2][5]],
            [symbols[0][0], symbols[1][2], symbols[2][4], symbols[3][5]],
            [symbols[1][1], symbols[2][3], symbols[3][4]],
            [symbols[1][0], symbols[2][2], symbols[3][3], symbols[4][3]],
            [symbols[2][1], symbols[3][2], symbols[4][2],],
            [symbols[2][0], symbols[3][1], symbols[4][1], symbols[5][1]],
            [symbols[3][0], symbols[4][0], symbols[5][0],],
        ]
    }

    private tumbleLeft2Right():gsap.core.Timeline {

        this._symbols = this.flipLeft(this._symbols);

        for (let i:number = this._symbols.length - 1; i >= 0; i--) {

            let localPop:SimpleReelModelSymbolData[] = [];

            for (let j:number = this._symbols[i].length - 1; j >= 0; j--) {
                const symbol = this._symbols[i][j];
                if (symbol.removed) {
                    const spliced = this._symbols[i].splice(j, 1);
                    localPop.unshift(spliced[0]);
                }
            }

            for (let sym of localPop) {
                this._symbols[i].unshift(sym);
            }

        }

        this._symbols = this.flipBackFromLeft(this._symbols);

        return this.animate(SimpleSymbolState.COLLAPSE_L2R, true);
    }
    private tumbleRight2Left():gsap.core.Timeline {

        this._symbols = this.flipRight(this._symbols);

        for (let i:number = this._symbols.length - 1; i >= 0; i--) {

            let localPop:SimpleReelModelSymbolData[] = [];

            for (let j:number = this._symbols[i].length - 1; j >= 0; j--) {
                const symbol = this._symbols[i][j];
                if (symbol.removed) {
                    const spliced = this._symbols[i].splice(j, 1);
                    localPop.unshift(spliced[0]);
                }
            }

            for (let sym of localPop) {
                this._symbols[i].unshift(sym);
            }
        }

        this._symbols = this.flipBackFromRight(this._symbols);

        return this.animate(SimpleSymbolState.COLLAPSE_R2L, false);
    }

    private animateNew(symbols:SimpleReelModelSymbolData[][], rowDelay:number, symdelay:number, state:SimpleSymbolState, symLandCallback:(symbolData:SimpleReelModelSymbolData, data:SymbolStopCallBackData)=>gsap.core.Timeline, callbackData:SymbolStopCallBackData, reelLength:number, isAnticipation:boolean=false):gsap.core.Timeline {

        const rows:gsap.core.Timeline[] = [];
        const tl = gsap.timeline();
        if (isAnticipation) {
           tl.add(() => {TheCage.reelView.startAnticipation()});
        }

        for (let i:number = 0; i < symbols.length; i++) {
            const drops:gsap.core.Timeline[] = [];

            for (let j:number = 0; j < symbols[i].length; j++) {
                const symbol = symbols[i][j];

                const dropTl = gsap.timeline();
                dropTl.add(() => {
                    symbol.positionable.setAvalancheState(state);
                })
                dropTl.add(gsap.to(symbol.positionable.position, {
                    duration:TheCageStatics.FILL_UP_DROP_DURATION * reelLength,
                    x:this._symbolTargetPositions[symbol.reelId][symbol.reelPosition].x,
                    y:this._symbolTargetPositions[symbol.reelId][symbol.reelPosition].y,
                    ease:"power1.in"
                }));
                dropTl.add(() => {
                    symbol.positionable.setAvalancheState(SimpleSymbolState.NORMAL);
                })
                dropTl.add(symLandCallback(symbol, callbackData));
                drops.push(dropTl);
            }


            if (drops.length > 0) {
                if (isAnticipation) {
                    rows.push(gsap.timeline().add(() => {TheCage.reelView.startAnticipationReel(i)}));
                }
                rows.push(GSAPUtils.createTimelineFromArray(drops, `<${symdelay}`))
                if (isAnticipation) {
                    rows.push(gsap.timeline().add(() => {TheCage.reelView.stopAnticipationReel(i)}));
                }
            }
        }
        tl.add(GSAPUtils.createTimelineFromArray(rows, isAnticipation ? '>' : `<${rowDelay}`));
        if (isAnticipation) {
            tl.add(() => {TheCage.reelView.stopAnticipation()});
        }
        return tl
    }

    public dropOut():gsap.core.Timeline {
        if (this.dropOutTl && this.dropOutTl.isActive()){
            this.dropOutTl.seek("ending",false);
            delete this.dropOutTl;
        }

        const dropOutSymbols = MatrixUtils.cloneMatrix(this._symbols);
        this._symbols = [];

        const tl = gsap.timeline({
            onComplete:() => {
                this.clearSymbols(dropOutSymbols);
            }
        });

        for (let i:number = 0; i < dropOutSymbols.length; i++) {

            const startDelayIndex = Math.floor(i / 2);

            for (let j:number = 0; j < dropOutSymbols[i].length; j++) {

                const symbol = dropOutSymbols[i][j];
                const dropTl = gsap.timeline();
                dropTl.add(() => {
                    symbol.positionable.setAvalancheState(SimpleSymbolState.DROP_OUT);
                })
                dropTl.add(gsap.to(symbol.positionable.position, {
                    duration:TheCageStatics.DROP_OUT_DURATION,
                    x:this._symbolTargetPositions[i][j].x - TheCageStatics.STEP_WIDTH * 7,
                    y:this._symbolTargetPositions[i][j].y + TheCageStatics.STEP_HEIGHT * 7,
                    ease:"power1.in"
                }));

                tl.add(dropTl, TheCageStatics.SYMBOL_DROP_DELAY * (startDelayIndex + j));

            }
        }

        tl.addLabel("ending");
        tl.add(gsap.to(this, {duration:0.0001}));
        this.dropOutTl = tl;
        return tl;
    }


    public dropIn(symbolNames:string[][]):gsap.core.Timeline {
        const symLandCallback = ReelUtils.symbolStopDropIn;
        const nearWinToggle = ReelUtils.nearWinToggle;
        const nearWinAllowed = TheCage.model.isNearWinAllowed();
        let scatters = 0;
        const reelLength = 7;

        //Create symbols
        for (let i:number = 0; i < symbolNames.length; i++) {
            this._symbols[i] = [];
            for (let j:number = 0; j < symbolNames[i].length; j++) {
                const symbol = this.createNewSymbol(symbolNames[i][j], i, j, SimpleReelModelState.IDLE)
                //symbol.positionable.setAvalancheState(SimpleSymbolState.NORMAL);
                this._symbols[i].push(symbol);
            }
        }

        this._symbols = this.flipRight(this._symbols);

        let symbolsBeforeNearWin:SimpleReelModelSymbolData[][] = [];
        let symbolsToNearWin:SimpleReelModelSymbolData[][] = [];
        let symbolsAfterNearWin:SimpleReelModelSymbolData[][] = [];
        for (let i:number = 0; i < this._symbols.length; i++) {
            symbolsBeforeNearWin[i] = [];
            symbolsToNearWin[i] = [];
            symbolsAfterNearWin[i] = [];

            for (let j:number = this._symbols[i].length - 1; j >= 0; j--) {
                const symbol = this._symbols[i][j];
                const newSymbol = this._symbols[i][j];

                symbol.positionable.position.x = this._symbolTargetPositions[symbol.reelId][symbol.reelPosition].x + TheCageStatics.STEP_WIDTH * reelLength;
                symbol.positionable.position.y = this._symbolTargetPositions[symbol.reelId][symbol.reelPosition].y - TheCageStatics.STEP_HEIGHT * reelLength;

                if (scatters < 2){
                    symbolsBeforeNearWin[i].push(symbol);
                } else if (scatters == 2 && nearWinAllowed){
                    symbolsToNearWin[i].push(symbol);
                } else {
                    symbolsAfterNearWin[i].push(symbol);
                }

                if (newSymbol.name == "S1" || newSymbol.name == "S2"){
                    scatters++;
                }

            }
        }

        this._symbols = this.flipBackFromRight(this._symbols);

        //flattens and adds as one row
        const symbolsToNearWinNonFlat = symbolsToNearWin;
        //symbolsToNearWin = [symbolsToNearWin.reduce((acc:SimpleReelModelSymbolData[], val:SimpleReelModelSymbolData[]) => acc.concat(val), [])];


        const jurPadding = TheCage.gameFlowController.calculatePaddingDuration(0);
        let isSingleDrop = false;
        let reelDelay = 0.1;
        let symDelay = 0.0;

        //0.9 is the shortest "normal" drop in.
        if (jurPadding > 0.89){
            isSingleDrop = true;
            symDelay = 0.05;
            reelDelay = jurPadding * 1000;
            reelDelay -= 280;
            reelDelay /= 5;
            reelDelay = Math.ceil(reelDelay);
            reelDelay /= 1000;
        }

        const tl = gsap.timeline();
        tl.add(this.animateNew(symbolsBeforeNearWin,reelDelay, symDelay, SimpleSymbolState.DROP_IN, symLandCallback, {isNearWin:false, isSingleSymbolDrop:isSingleDrop}, reelLength));

        if (symbolsToNearWin.length > 0 && symbolsToNearWin.some((reel) => reel.length > 0)) {//&& symbolsToNearWin[0].length > 0) {
            const landedFinalScatter = this.getAllSymbolsWithName("S1").length == 3
            //tl.add(TheCage.reelView.scatterAttention(symbolsToNearWinNonFlat, landedFinalScatter));
            tl.add(this.animateNew(symbolsToNearWin, 0.0, TheCageStatics.FILL_UP_SYMBOL_NEAR_WIN_DELAY, SimpleSymbolState.DROP_IN, symLandCallback, {isNearWin:true, isSingleSymbolDrop:isSingleDrop}, reelLength, true));
        }

        if (tl.duration() >= jurPadding){
            //We have already spent all required time on symbols + nearwin symbols.
            reelDelay = 0.1;
            symDelay = 0.0;
            isSingleDrop = false;
        }
        tl.add(this.animateNew(symbolsAfterNearWin, reelDelay, symDelay, SimpleSymbolState.DROP_IN, symLandCallback,{isNearWin:false, isSingleSymbolDrop:isSingleDrop}, reelLength));
        return tl;
    }

    public collapse():gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(this.tumbleLeft2Right());
        tl.add(this.tumbleRight2Left());
        return tl;
    }

    public fillUp(symbolNames:string[][], stateBeforeFillUp:string[][], nearWinAllowed:boolean):gsap.core.Timeline {
        let popped:SimpleReelModelSymbolData[] = [];
        const symLandCallback = ReelUtils.symbolStopFillUp;

        const stateMatrix = new StateMatrix<string>(stateBeforeFillUp);
        const scatterPositions = stateMatrix.getAllCoordinatesForValues(["S1","S2"]);
        let unLockedScatters = scatterPositions.length;

        this._symbols = this.flipRight(this._symbols);
        stateMatrix.matrix = this.flipRight(stateMatrix.matrix);

        const reelLength = 7;

        let symbolsBeforeNearWin:SimpleReelModelSymbolData[][] = [];
        let symbolsToNearWin:SimpleReelModelSymbolData[][] = [];
        let symbolsAfterNearWin:SimpleReelModelSymbolData[][] = [];
        for (let i:number = 0; i < this._symbols.length; i++) {
            symbolsBeforeNearWin[i] = [];
            symbolsToNearWin[i] = [];
            symbolsAfterNearWin[i] = [];

            const localNewSyms:SimpleReelModelSymbolData[] = [];
            for (let j:number = this._symbols[i].length - 1; j >= 0; j--) {
                const symbol = this._symbols[i][j];
                if (symbol.removed) {
                    const spliced = this._symbols[i].splice(j, 1);
                    popped.unshift(spliced[0]);
                    const newSymbol:SimpleReelModelSymbolData = this.createNewSymbol(symbolNames[symbol.reelId][symbol.reelPosition], symbol.reelId, symbol.reelPosition, SimpleReelModelState.FILL_UP)
                    newSymbol.positionable.position.x = this._symbolTargetPositions[symbol.reelId][symbol.reelPosition].x + TheCageStatics.STEP_WIDTH * (reelLength + 1);
                    newSymbol.positionable.position.y = this._symbolTargetPositions[symbol.reelId][symbol.reelPosition].y - TheCageStatics.STEP_HEIGHT * (reelLength + 1);
                    localNewSyms.push(newSymbol);


                    if (unLockedScatters < 2){
                        symbolsBeforeNearWin[i].push(newSymbol);
                    } else if (unLockedScatters == 2 && nearWinAllowed){
                        symbolsToNearWin[i].push(newSymbol);
                    } else {
                        symbolsAfterNearWin[i].push(newSymbol);
                    }

                    if (newSymbol.name == "S1" || newSymbol.name == "S2"){
                        unLockedScatters++;
                    }
                }
            }

            for (let newSym of localNewSyms) {
                this._symbols[i].unshift(newSym);
            }

        }
        this._symbols = this.flipBackFromRight(this._symbols);

        //flattens and adds as one row.
        //const symbolsToNearWinNonFlat = symbolsToNearWin;
        //symbolsToNearWin = [symbolsToNearWin.reduce((acc:SimpleReelModelSymbolData[], val:SimpleReelModelSymbolData[]) => acc.concat(val), [])];

        const tl = gsap.timeline();
        tl.add(this.animateNew(symbolsBeforeNearWin, 0.0, TheCageStatics.FILL_UP_SYMBOL_DELAY, SimpleSymbolState.DROP_IN, symLandCallback, {isNearWin:false, isSingleSymbolDrop:true}, reelLength));

        if (symbolsToNearWin.length > 0 && symbolsToNearWin.some((reel) => reel.length > 0)) {//&& symbolsToNearWin[0].length > 0) {
            const landedFinalScatter = this.getAllSymbolsWithName("S1").length == 3
            //tl.add(TheCage.reelView.scatterAttention(symbolsToNearWinNonFlat, landedFinalScatter));
            tl.add(this.animateNew(symbolsToNearWin, 0.0, TheCageStatics.FILL_UP_SYMBOL_NEAR_WIN_DELAY, SimpleSymbolState.DROP_IN, symLandCallback,{isNearWin:true, isSingleSymbolDrop:true}, reelLength, true));
        }

        tl.add(this.animateNew(symbolsAfterNearWin, 0.0, TheCageStatics.FILL_UP_SYMBOL_DELAY, SimpleSymbolState.DROP_IN, symLandCallback, {isNearWin:false, isSingleSymbolDrop:true}, reelLength));
        tl.add(() => {
            this.clearSymbols([popped]);
        })
        return tl;
    }

    public swapSymbol(newName:string, reel:number, position:number):{oldSymbol:SimpleReelModelSymbolData, newSymbol:SimpleReelModelSymbolData} {
        const oldSymbol = this._symbols[reel][position];
        const newSymbol:SimpleReelModelSymbolData = this.createNewSymbol(newName, oldSymbol.reelId, oldSymbol.reelPosition, SimpleReelModelState.IDLE);
        newSymbol.positionable.position.x = oldSymbol.positionable.position.x;
        newSymbol.positionable.position.y = oldSymbol.positionable.position.y;

        this._symbols[reel][position] = newSymbol;

        return {
            oldSymbol:oldSymbol,
            newSymbol:newSymbol
        };
    }

    public getAllVisibleSymbols():SimpleReelModelSymbolData[][] {
        return this._symbols;
    }

    public getAllSymbolsWithNames(names:string[]):SimpleReelModelSymbolData[]{
        const result:SimpleReelModelSymbolData[] = [];

        for (let reel of this._symbols){
            for (let sym of reel){
                if (names.indexOf(sym.name) >= 0){
                    result.push(sym);
                }
            }
        }

        return result;
    }

    public getAllSymbolsWithName(name:string):SimpleReelModelSymbolData[]{
        const result:SimpleReelModelSymbolData[] = [];
        for (let reel of this._symbols){
            for (let sym of reel){
                if (sym.name == name){
                    result.push(sym);
                }
            }
        }
        return result;
    }

    public getAllSymbolsWithNamesFlippedRight(names:string[]):SimpleReelModelSymbolData[]{
        this._symbols = this.flipRight(this._symbols);
        const result = this.getAllSymbolsWithNames(names);
        this._symbols = this.flipBackFromRight(this._symbols);
        return result;
    }

    public getSymbolsAt(coords:Coordinate2d[]):SimpleReelModelSymbolData[] {
        const result:SimpleReelModelSymbolData[] = [];
        for (let coord of coords){
            result.push(this._symbols[coord.x][coord.y]);
        }
        return result;
    }

    public getSymbolAt(coord:Coordinate2d):SimpleReelModelSymbolData {
        return this._symbols[coord.x][coord.y];
    }

    public sanityCheck(data:string[][]):{coords:Coordinate2d[], nameMatrix:string[][]} {
       const failed:Coordinate2d[] = []

        const nameMatrix:string[][] = [];

        for (let i:number = 0; i < this._symbols.length; i++){
            nameMatrix[i] = [];
            for (let j:number = 0; j < this._symbols[i].length; j++){
                nameMatrix[i][j] = this._symbols[i][j].removed ? "BLANK" : this._symbols[i][j].name;
                if (this._symbols[i][j].name != data[i][j]){
                    if (this._symbols[i][j].removed && data[i][j] == "BLANK"){
                        //this is ok
                    } else {
                        failed.push({x:i, y:j});
                    }
                }
            }
        }

        return {
            coords:failed,
            nameMatrix: nameMatrix
        };

    }
}